import { createClient } from '@supabase/supabase-js';
import toast from 'react-hot-toast';

// Cache configuration
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
const queryCache = new Map<string, { data: any; timestamp: number }>();

const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1 second

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables');
}

// Cache helper functions
const getCachedData = (key: string) => {
  const cached = queryCache.get(key);
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    return cached.data;
  }
  return null;
};

const setCachedData = (key: string, data: any) => {
  queryCache.set(key, { data, timestamp: Date.now() });
};

const clearCache = () => {
  queryCache.clear();
};

async function fetchWithRetry(url: string, options: any, retries = MAX_RETRIES): Promise<Response> {
  try {
    const response = await fetch(url, options);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response;
  } catch (error) {
    if (retries > 0) {
      await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
      return fetchWithRetry(url, options, retries - 1);
    }
    throw error;
  }
}

// Enhanced Supabase client with caching
const enhancedFetch = async (...args: Parameters<typeof fetch>): Promise<Response> => {
  const [url, options] = args;
  const cacheKey = `${options?.method || 'GET'}-${url}`;

  // Only cache GET requests
  if (options?.method === undefined || options?.method === 'GET') {
    const cachedData = getCachedData(cacheKey);
    if (cachedData) {
      return new Response(JSON.stringify(cachedData), {
        headers: { 'Content-Type': 'application/json' }
      });
    }
  }

  try {
    const response = await fetchWithRetry(url, options);
    
    // Cache successful GET responses
    if ((options?.method === undefined || options?.method === 'GET') && response.ok) {
      const data = await response.clone().json();
      setCachedData(cacheKey, data);
    }
    
    return response;
  } catch (err) {
    throw err;
  }
};

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    storage: window.localStorage
  },
  global: {
    fetch: enhancedFetch
  }
});